import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Map as MapIcon, Compass, Anchor, ArrowRight } from 'lucide-react';
import { StateGrid } from './components/StateGrid';
import { SEO } from '../../components/SEO';
import { Logo } from '../../components/Logo';
import { useAdaptiveLayout } from '../../core/hooks/useAdaptiveLayout';
import { FeedPage } from '../social/pages/FeedPage';
import { LegendBadge } from '../../components/LegendBadge';
import { FISHING_LEGENDS } from '../../data/legends';
import { TenantCard } from './components/TenantCard';
import { TenantListingCard } from '../../core/types/portal';
import { VIPGreetingBanner } from './components/VIPGreetingBanner';

export const PortalHome: React.FC = () => {
  const navigate = useNavigate();
  const { isPortrait, isMobile, isTablet } = useAdaptiveLayout();

  // Se estiver na Vertical e for Mobile ou Tablet -> Mostrar Feed Social
  if (isPortrait && (isMobile || isTablet)) {
    return <FeedPage />;
  }

  const featuredDestinations = [
    { state: 'AM', name: 'Amazonas', description: 'Paraíso do Tucunaré', image: '🌴' },
    { state: 'GO', name: 'Goiás', description: 'Vale do Araguaia', image: '🏞️' },
    { state: 'MS', name: 'Mato Grosso do Sul', description: 'Pantanal Selvagem', image: '🐊' },
    { state: 'MT', name: 'Mato Grosso', description: 'Rios de Gigantes', image: '🐅' },
    { state: 'PA', name: 'Pará', description: 'A Força do Tapajós', image: '🛶' },
  ];

  // Mock conversion of legends to TenantListingCard for display
  const legendTenants: TenantListingCard[] = FISHING_LEGENDS.slice(0, 3).map(legend => ({
    id: legend.id,
    slug: legend.id,
    businessName: legend.fullName,
    tenantType: 'influenciador',
    location: {
      city: legend.biography.hometown.split(',')[0],
      state: legend.biography.hometown.split(',')[1]?.trim() || 'BR',
      touristicRegion: 'Hall da Fama'
    },
    shortDescription: legend.biography.achievements[0],
    isFeatured: true,
    verifiedBadge: true,
    isFishingLegend: true,
    legendTier: legend.type,
    highlights: legend.biography.specialties,
    categoryTags: ['pesca-esportiva']
  }));

  return (
    <>
      <SEO 
        title="Nossa Pesca - O seu guia de pesca definitivo" 
        description="Encontre as melhores pousadas, guias e destinos de pesca esportiva no Brasil. Planeje sua próxima pescaria no Nossa Pesca.com."
      />

      <VIPGreetingBanner />
      
      <main className="max-w-[1400px] mx-auto px-6 py-32 space-y-32">
        {/* Intro Section - Minimalist */}
        {/* Intro Section - Minimalist (REMOVED per design update) */}
        {/* <header className="max-w-4xl animate-fade-in">
          <Logo className="w-16 h-16 mb-12" />
          <h1 className="text-5xl md:text-7xl font-black tracking-tightest leading-tight mb-8">
            Para onde a sua <span className="text-brand-gold italic">paixão</span> te leva?
          </h1>
          <p className="text-xl text-white/40 max-w-2xl font-medium leading-relaxed">
            Descubra as joias escondidas da pesca brasileira. De hotéis luxuosos no Araguaia a guias nativos na Serra da Mesa.
          </p>
        </header> */}

        <section className="space-y-12">
          {/* Featured Destinations */}
          <div className="flex items-center gap-6 mb-12">
             <div className="h-px w-12 bg-brand-gold" />
             <h2 className="text-xs font-black uppercase tracking-[0.5em] text-white/30">Destinos em Destaque</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {featuredDestinations.map(dest => (
              <button
                key={dest.state}
                onClick={() => navigate(`/estados/${dest.state}`)}
                className="group relative h-[300px] rounded-[2.5rem] overflow-hidden border border-white/5 bg-brand-charcoal/30 flex flex-col p-8 text-left transition-all hover:border-brand-gold/30 hover:scale-[1.02]"
              >
                <div className="text-4xl mb-auto">{dest.image}</div>
                <div>
                  <h3 className="text-2xl font-black text-white group-hover:text-brand-gold transition-colors">{dest.name}</h3>
                  <p className="text-sm text-white/30 font-bold uppercase tracking-widest">{dest.description}</p>
                </div>
                <div className="absolute top-8 right-8 w-10 h-10 rounded-full bg-white/5 items-center justify-center hidden group-hover:flex">
                   <ArrowRight size={20} className="text-brand-gold" />
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Hall of Fame / Legends Section */}
        <section className="space-y-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
               <div className="h-px w-12 bg-amber-400" />
               <h2 className="text-xs font-black uppercase tracking-[0.5em] text-amber-400/50">Hall da Fama</h2>
            </div>
            <button 
              onClick={() => navigate('/influenciadores')}
              className="text-white/40 hover:text-white text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors"
            >
              Ver todos <ArrowRight size={14} />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-8 max-w-xl">
              <h2 className="text-4xl md:text-5xl font-black leading-tight">
                As <span className="text-amber-400">Lendas</span> que inspiram gerações.
              </h2>
              <p className="text-white/40 font-medium leading-relaxed">
                Conheça os mestres que transformaram a pesca brasileira em arte. Técnicas exclusivas, histórias de gigantes e o selo de autenticidade Nossa Pesca.
              </p>
            </div>
            
            <div className="flex flex-col gap-4">
              {legendTenants.map(tenant => (
                <TenantCard key={tenant.id} tenant={tenant} variant="compact" />
              ))}
            </div>
          </div>
        </section>

        {/* Global Directory */}
        <section className="space-y-12 pb-48">
          <div className="flex items-center gap-6 mb-12">
             <div className="h-px w-12 bg-white/10" />
             <h2 className="text-xs font-black uppercase tracking-[0.5em] text-white/30">Explore o Brasil</h2>
          </div>
          <StateGrid navigate={navigate} />
        </section>
      </main>

    </>
  );
};
